"use client";

import { useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  SquareCode,
  Link2,
  Info,
  Eye,
  Pencil,
  Palette,
  Highlighter,
  ThumbsUp,
  TriangleAlert,
  Table,
  Volume2,
} from "lucide-react";
import Markdown from "@/components/Markdown";
import { HIGHLIGHT_COLORS, TEXT_COLORS, type TextStyleKind } from "@/lib/lesson-content/text-styles";
import { emptyTable, findTableAt, insertPoint, insertTable, type TableModel } from "@/lib/lesson-content/table-markdown";
import TableEditor from "@/components/admin/lesson-builder/TableEditor";

function ToolbarButton({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      // Keep the textarea's selection when a toolbar button is clicked.
      onMouseDown={(ev) => ev.preventDefault()}
      onClick={onClick}
      className="rounded p-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    >
      {children}
    </button>
  );
}

/** A toolbar button that opens a row of colour swatches; picking one wraps the selection. */
function SwatchMenu({
  kind,
  onPick,
}: {
  kind: TextStyleKind;
  onPick: (tone: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const palette: Record<string, { label: string; hex: string }> = kind === "color" ? TEXT_COLORS : HIGHLIGHT_COLORS;
  const title = kind === "color" ? "Text colour" : "Highlight";
  const Icon = kind === "color" ? Palette : Highlighter;

  useEffect(() => {
    if (!open) return;
    const close = (ev: MouseEvent) => {
      if (!box.current?.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={box} className="relative">
      <ToolbarButton onClick={() => setOpen((v) => !v)} title={title}>
        <Icon className="h-3.5 w-3.5" />
      </ToolbarButton>
      {open ? (
        <div className="absolute left-0 top-full z-20 mt-1 flex gap-1.5 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          {Object.entries(palette).map(([tone, { label, hex }]) => (
            <button
              key={tone}
              type="button"
              title={`${title}: ${label}`}
              aria-label={`${title}: ${label}`}
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => {
                onPick(tone);
                setOpen(false);
              }}
              className="h-6 w-6 rounded-full border border-slate-300 transition-transform hover:scale-110"
              style={{ backgroundColor: hex }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Safe rich-text editor for the rich_text block: a formatting toolbar over a
 * markdown textarea, plus a Write/Preview toggle. It stores markdown verbatim
 * (no re-serialization), so custom syntax the toolbar has no button for
 * (LaTeX, mermaid, extra :::callouts) is simply typed and round-trips intact.
 * Preview renders through our own <Markdown> so it matches the student view.
 */
export default function MarkdownField({
  value,
  onChange,
  rows = 10,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [tab, setTab] = useState<"write" | "preview">("write");
  /** The open table editor: a new table at `cursor`, or the existing one at `start`–`end`. */
  const [tableEdit, setTableEdit] = useState<
    { table: TableModel; cursor: number; range: { start: number; end: number } | null } | null
  >(null);

  const restore = (start: number, end: number) => {
    requestAnimationFrame(() => {
      const ta = ref.current;
      if (!ta) return;
      ta.focus();
      ta.selectionStart = start;
      ta.selectionEnd = end;
    });
  };

  /** Wrap the selection (or a placeholder) in `before`/`after`. */
  const wrap = (before: string, after = before, placeholder = "text") => {
    const ta = ref.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value: v } = ta;
    const sel = v.slice(s, e) || placeholder;
    onChange(v.slice(0, s) + before + sel + after + v.slice(e));
    restore(s + before.length, s + before.length + sel.length);
  };

  /**
   * Wrap in an inline tag. Tags must sit on one line, so a blank or multi-line
   * selection becomes a placeholder at the cursor, and spaces stay outside the tag.
   */
  const wrapInline = (before: string, after: string) => {
    const ta = ref.current;
    if (!ta) return;
    const { value: v } = ta;
    let { selectionStart: s, selectionEnd: e } = ta;
    const raw = v.slice(s, e);
    if (!raw.trim() || raw.includes("\n")) {
      e = s;
    } else {
      s += raw.length - raw.trimStart().length;
      e -= raw.length - raw.trimEnd().length;
    }
    const sel = v.slice(s, e) || "text";
    onChange(v.slice(0, s) + before + sel + after + v.slice(e));
    restore(s + before.length, s + before.length + sel.length);
  };

  /** Prefix every line touched by the selection (headings, lists, quote). */
  const linePrefix = (prefix: string) => {
    const ta = ref.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value: v } = ta;
    const lineStart = v.lastIndexOf("\n", s - 1) + 1;
    const block = v.slice(lineStart, e);
    const prefixed = block
      .split("\n")
      .map((l) => prefix + l)
      .join("\n");
    onChange(v.slice(0, lineStart) + prefixed + v.slice(e));
    restore(s + prefix.length, e + (prefixed.length - block.length));
  };

  /**
   * Insert a standalone block (code fence, callout) on its own lines, with any selected text
   * moved inside it. `text` is the opening line, an empty body line and the closing line; the
   * cursor lands on the body line so the author can type straight away.
   */
  const insertBlock = (text: string) => {
    const ta = ref.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value: v } = ta;
    const pad = s > 0 && v[s - 1] !== "\n" ? "\n" : "";
    const opening = text.slice(0, text.indexOf("\n") + 1);
    const closing = text.slice(text.indexOf("\n") + 2);
    const body = v.slice(s, e).replace(/\n+$/, "");
    const snippet = pad + opening + body + "\n" + closing;
    onChange(v.slice(0, s) + snippet + v.slice(e));
    const bodyStart = s + pad.length + opening.length;
    restore(bodyStart, bodyStart + body.length);
  };

  /** Open the table editor on the table under the cursor, or on a new one. */
  const openTable = () => {
    const ta = ref.current;
    if (!ta) return;
    const found = findTableAt(ta.value, ta.selectionStart);
    setTableEdit(
      found
        ? { table: found.table, cursor: ta.selectionStart, range: { start: found.start, end: found.end } }
        : { table: emptyTable(), cursor: insertPoint(ta.value, ta.selectionStart), range: null },
    );
  };

  const saveTable = (markdown: string) => {
    if (!tableEdit) return;
    const { range, cursor } = tableEdit;
    // The offsets came from the textarea's text, which has \r\n normalised to \n; splice that,
    // not the stored value, or CRLF content would shift them.
    const text = ref.current?.value ?? value;
    if (range) {
      onChange(text.slice(0, range.start) + markdown + text.slice(range.end));
      restore(range.start, range.start + markdown.length);
    } else {
      const next = insertTable(text, cursor, markdown);
      onChange(next.text);
      restore(next.start, next.end);
    }
    setTableEdit(null);
  };

  const insertLink = () => {
    const ta = ref.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value: v } = ta;
    const label = v.slice(s, e) || "link text";
    const md = `[${label}](https://)`;
    onChange(v.slice(0, s) + md + v.slice(e));
    // Select the URL placeholder so the author can type over it.
    const urlStart = s + label.length + 3;
    restore(urlStart, urlStart + 8);
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-2 py-1">
        <div className={`flex flex-wrap items-center gap-0.5 ${tab === "preview" ? "invisible" : ""}`}>
          <ToolbarButton onClick={() => wrap("**")} title="Bold"><Bold className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => wrap("*")} title="Italic"><Italic className="h-3.5 w-3.5" /></ToolbarButton>
          <span className="mx-1 h-4 w-px bg-slate-200" />
          <ToolbarButton onClick={() => linePrefix("## ")} title="Heading"><Heading2 className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => linePrefix("### ")} title="Subheading"><Heading3 className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => linePrefix("- ")} title="Bulleted list"><List className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => linePrefix("1. ")} title="Numbered list"><ListOrdered className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => linePrefix("> ")} title="Quote"><Quote className="h-3.5 w-3.5" /></ToolbarButton>
          <span className="mx-1 h-4 w-px bg-slate-200" />
          <ToolbarButton onClick={insertLink} title="Link"><Link2 className="h-3.5 w-3.5" /></ToolbarButton>
          <SwatchMenu kind="color" onPick={(tone) => wrapInline(":color[", `]{${tone}}`)} />
          <SwatchMenu kind="highlight" onPick={(tone) => wrapInline(":highlight[", `]{${tone}}`)} />
          <span className="mx-1 h-4 w-px bg-slate-200" />
          <ToolbarButton onClick={() => wrap("`", "`", "code")} title="Inline code"><Code className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => insertBlock("```\n\n```\n")} title="Code block"><SquareCode className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => insertBlock(":::info\n\n:::\n")} title="Callout (info)"><Info className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => insertBlock(":::good Good for\n\n:::\n")} title="Green box (good for)"><ThumbsUp className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => insertBlock(":::warning Watch out\n\n:::\n")} title="Amber box (watch out)"><TriangleAlert className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={openTable} title="Table: insert a new one, or put the cursor in a table to edit it">
            <Table className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton onClick={() => insertBlock(":::listen Audio sample\nText to read aloud\n:::\n")} title="Read-aloud box (a Play button reads the text)">
            <Volume2 className="h-3.5 w-3.5" />
          </ToolbarButton>
        </div>
        <div className="flex items-center gap-1 text-[11px] font-semibold">
          <button
            type="button"
            onClick={() => setTab("write")}
            className={`flex items-center gap-1 rounded px-2 py-1 ${tab === "write" ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
          >
            <Pencil className="h-3 w-3" /> Write
          </button>
          <button
            type="button"
            onClick={() => setTab("preview")}
            className={`flex items-center gap-1 rounded px-2 py-1 ${tab === "preview" ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
          >
            <Eye className="h-3 w-3" /> Preview
          </button>
        </div>
      </div>

      {tab === "write" ? (
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder="Prose, tables, code, LaTeX ($x^2$), mermaid, :::callouts…"
          className="w-full resize-y rounded-b-md border-0 bg-white px-3 py-2 font-mono text-sm text-slate-800 focus:outline-none focus:ring-0"
        />
      ) : (
        <div className="ile-runner max-h-[28rem] overflow-y-auto px-4 py-3">
          {value.trim() ? (
            <Markdown content={value} variant="lesson" />
          ) : (
            <p className="text-sm italic text-slate-400">Nothing to preview yet.</p>
          )}
        </div>
      )}
      {tableEdit ? (
        <TableEditor initial={tableEdit.table} isEdit={tableEdit.range !== null} onSave={saveTable} onClose={() => setTableEdit(null)} />
      ) : null}
    </div>
  );
}
